// W36: thin wrapper over the generated channel-wa client (templates half).
//
// W4: typed access to /v1/templates. Lifecycle: draft → submit → submitted
// → (Meta async) → approved | rejected. Meta callback simulation endpoint
// is gated to dev/smoke only via the absence of a Meta-side approval poller
// in front of it.
//
// Types come from `./generated/channel-wa` (templates are served by
// `services/channel-wa`, so they share that service's OpenAPI document;
// regenerate with `pnpm --filter @repletoryreach/sdk-api generate channel-wa`).
// The request path stays on the house core so the envelope, Idempotency-Key,
// token refresh and ApiError mapping behave exactly as before.
//
// The annotation pass verified this module field-for-field against
// `services/channel-wa/Endpoints/TemplateEndpoints.cs`: the cutover is a
// straight swap, with the two shape notes below the only deviations.

import { http } from './_core/request.js';
import type { Envelope } from './_core/envelope.js';
import type * as gen from './generated/channel-wa/index.js';

// ── Types (generated) ──────────────────────────────────────────────────────

// Platform-wide: every service configures
// `JsonSerializerOptions.DefaultIgnoreCondition = WhenWritingNull`, so a null
// field is ABSENT from the JSON rather than present as `null`. The generated
// response types still declare those members as `null | T` and required
// read them defensively, never `in`-test them.

/**
 * The service's request records take their optional members as plain nullable
 * C# parameters (`string? Category`), which OpenAPI reports as *required and
 * nullable*: so the generated request types make them mandatory even though
 * System.Text.Json binds an absent key to `null`. `Optional<T, K>` restores
 * the optionality the wire actually has, and that the consoles have always
 * compiled against.
 */
type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/**
 * The RepletoryReach lifecycle vocabulary, mirroring
 * `MessageTemplateStatuses` in `services/channel-wa/Domain/MessageTemplate.cs`.
 * Kept hand-written: the C# constants are a static class, not an enum, so the
 * OpenAPI document types `status` as a bare `string`.
 */
export type MessageTemplateStatus =
  | 'draft'
  | 'submitted'
  | 'approved'
  | 'rejected'
  | 'paused';

/**
 * A template row: `gen.TemplateDto` verbatim except for `status`.
 *
 * `components` is a JSONB **string** (an array of
 * `{ type, format?, text, buttons? }` per Meta's components schema), not a
 * parsed object; `meta_status` is Meta's own review word (`APPROVED`,
 * `REJECTED`, `PENDING`, …, plus `APPROVED_DEV_FALLBACK`) as last read from
 * the Graph API, while `status` is the RepletoryReach lifecycle value.
 * `dev_fallback` is true when `status` was set by the poller's non-Production
 * auto-approve fallback rather than by Meta: always false in Production, and
 * a template carrying it must be treated as unapproved for any real send.
 * `imported_at` is the last write by `importFromMeta()`.
 */
export type MessageTemplate = Omit<gen.TemplateDto, 'status'> & {
  /**
   * ⚠ DRIFT: the service declares this `string` (the values come from the
   * `MessageTemplateStatuses` static class, which OpenAPI cannot see as an
   * enum). Narrowed to the five real lifecycle values so the consoles'
   * `Record<Status, …>` lookup tables keep compiling.
   */
  status: MessageTemplateStatus;
};

export type CreateTemplateRequest = Optional<
  gen.CreateTemplateRequest,
  'category' | 'language' | 'components'
>;
export type UpdateTemplateRequest = Optional<gen.UpdateTemplateRequest, 'category' | 'components'>;
export type MetaCallbackRequest = Optional<gen.MetaCallbackRequest, 'reason'>;

/**
 * `waba_id` is the WhatsApp Business Account id to read the catalog from.
 * Optional: defaults to `waba_id` (else `business_id`) on the org's
 * `meta_whatsapp` integration row, then the `WhatsAppCloud:WabaId` service
 * config.
 */
export type ImportFromMetaRequest = Optional<gen.ImportFromMetaRequest, 'waba_id'>;

/**
 * Upsert tallies from `POST /v1/templates/import-from-meta`: `imported` is
 * templates Meta had that we did not (rows created), `updated` is templates we
 * already had where at least one field changed, `skipped` is templates that
 * already matched Meta exactly, and `total` is what Meta reported in all.
 */
export type ImportFromMetaResult = gen.TemplateImportResult;

// ── Operations ─────────────────────────────────────────────────────────────

export function list(): Promise<Envelope<MessageTemplate[]>> {
  return http.get<Envelope<MessageTemplate[]>>('/v1/templates');
}

export function get(id: string): Promise<Envelope<MessageTemplate>> {
  return http.get<Envelope<MessageTemplate>>(`/v1/templates/${encodeURIComponent(id)}`);
}

export function create(req: CreateTemplateRequest): Promise<Envelope<MessageTemplate>> {
  return http.post<Envelope<MessageTemplate>>('/v1/templates', { body: req });
}

export function update(id: string, req: UpdateTemplateRequest): Promise<Envelope<MessageTemplate>> {
  return http.patch<Envelope<MessageTemplate>>(`/v1/templates/${encodeURIComponent(id)}`, { body: req });
}

export function remove(id: string): Promise<void> {
  return http.delete<void>(`/v1/templates/${encodeURIComponent(id)}`);
}

export function submit(id: string): Promise<Envelope<MessageTemplate>> {
  return http.post<Envelope<MessageTemplate>>(`/v1/templates/${encodeURIComponent(id)}/submit`);
}

/**
 * Dev/smoke only: production receives Meta's approval/rejection through
 * the `TemplateStatusPoller` background worker, not this endpoint.
 */
export function metaCallback(id: string, req: MetaCallbackRequest): Promise<Envelope<MessageTemplate>> {
  return http.post<Envelope<MessageTemplate>>(
    `/v1/templates/${encodeURIComponent(id)}/meta-callback`,
    { body: req }
  );
}

/**
 * W4, pull the WABA's template catalog off the Meta Graph API and upsert it
 * into `message_templates` by (org, code, language). Meta's `name` becomes our
 * `code`, and Meta's status/category/components win over the local copy
 * Meta is the system of record once a template has been submitted.
 *
 * Fails with 400 `NO_WABA_ID` when no WABA id can be resolved, and 502
 * `META_ERROR` when the Graph API answers non-2xx. In Production, an
 * unconfigured `meta_whatsapp` integration answers 503 `not_configured`.
 */
export function importFromMeta(req?: ImportFromMetaRequest): Promise<Envelope<ImportFromMetaResult>> {
  return http.post<Envelope<ImportFromMetaResult>>('/v1/templates/import-from-meta', {
    body: req ?? {}
  });
}
